import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  approveProductivityAdjustment,
  fetchProductivityAdjustmentApprovalDetail,
  rejectProductivityAdjustment,
  updateProductivityAdjustmentRequestedTime,
} from '../../productivity/api/productivityApi'
import { extractApiErrorMessage } from '../../../utils/apiError'
import ProductivityAdjustmentDetails from './components/ProductivityAdjustmentDetails'
import ProductivityAdjustmentEditModal from './components/ProductivityAdjustmentEditModal'

export default function ProductivityAdjustmentApprovalDetailScreen() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const adjustmentId = Number(searchParams.get('id') || 0)
  const requestedReturnTo = location.state?.returnTo
  const returnTo = typeof requestedReturnTo === 'string'
    && requestedReturnTo.startsWith('/')
    && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : '/approval/productivity-hours/list'

  const [item, setItem] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isActing, setIsActing] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [rejectModal, setRejectModal] = useState({ open: false, remark: '' })
  const [isEditOpen, setIsEditOpen] = useState(false)

  const loadDetail = useCallback(async () => {
    if (!adjustmentId) {
      setError('A valid productivity adjustment is required.')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError('')
    try {
      setItem(await fetchProductivityAdjustmentApprovalDetail(adjustmentId))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to load productivity adjustment details.'))
    } finally {
      setIsLoading(false)
    }
  }, [adjustmentId])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  async function runDecision(type, decisionRemark = '') {
    setIsActing(true)
    setError('')
    setMessage('')
    try {
      if (type === 'approve') {
        await approveProductivityAdjustment(adjustmentId, {})
        setMessage('Productivity adjustment approved successfully.')
      } else {
        await rejectProductivityAdjustment(adjustmentId, { decisionRemark })
        setMessage('Productivity adjustment rejected successfully.')
        setRejectModal({ open: false, remark: '' })
      }
      await loadDetail()
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Approval action failed.'))
    } finally {
      setIsActing(false)
    }
  }

  async function saveEdit(payload) {
    setIsActing(true)
    setError('')
    setMessage('')
    try {
      await updateProductivityAdjustmentRequestedTime(adjustmentId, payload)
      setMessage('Productivity request updated successfully.')
      setIsEditOpen(false)
      await loadDetail()
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to update productivity request.'))
    } finally {
      setIsActing(false)
    }
  }

  const isPending = item?.approvalStatus === 'PENDING'

  return (
    <div className="mx-auto w-full max-w-[1200px] pb-10">
      <header className="mb-6 rounded-[2rem] bg-surface-container-low px-8 py-7">
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="inline-flex items-center gap-2 text-sm font-bold text-primary"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Back to Productivity Approval
        </button>
        <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Approval Console</p>
            <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface">Productivity Request Details</h1>
          </div>
          {item ? <span className="text-sm font-semibold text-on-surface-variant">Request #{item.id}</span> : null}
        </div>
      </header>

      {error ? <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{error}</div> : null}
      {message ? <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div> : null}

      <section className="rounded-3xl bg-surface-container-lowest p-6 shadow-sm">
        {isLoading ? (
          <div className="py-20 text-center text-sm text-on-surface-variant">Loading productivity request...</div>
        ) : item ? (
          <ProductivityAdjustmentDetails item={item} />
        ) : null}
      </section>

      {item && isPending && (item.canApprove || item.canEditRequestedTime) ? (
        <div className="mt-6 flex justify-end gap-3">
          {item.canEditRequestedTime ? <button type="button" disabled={isActing} onClick={() => setIsEditOpen(true)} className="rounded-full bg-secondary-container px-6 py-2.5 text-sm font-bold text-on-secondary-container disabled:opacity-50">Edit Request</button> : null}
          {item.canApprove ? (
            <>
              <button type="button" disabled={isActing} onClick={() => setRejectModal({ open: true, remark: '' })} className="rounded-full bg-error-container px-6 py-2.5 text-sm font-bold text-error disabled:opacity-50">Reject</button>
              <button type="button" disabled={isActing} onClick={() => runDecision('approve')} className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-on-primary disabled:opacity-50">{isActing ? 'Working...' : 'Approve'}</button>
            </>
          ) : null}
        </div>
      ) : null}

      <ProductivityAdjustmentEditModal
        item={isEditOpen ? item : null}
        isSaving={isActing}
        onClose={() => setIsEditOpen(false)}
        onSubmit={saveEdit}
      />

      {rejectModal.open ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-on-surface">Reject Productivity Adjustment</h2>
            <textarea
              rows={4}
              value={rejectModal.remark}
              onChange={(event) => setRejectModal((previous) => ({ ...previous, remark: event.target.value }))}
              className="mt-4 w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
              placeholder="Optional decision remark..."
            />
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setRejectModal({ open: false, remark: '' })} className="rounded-md bg-secondary-container px-5 py-2 text-sm font-bold text-on-secondary-container">Cancel</button>
              <button type="button" disabled={isActing} onClick={() => runDecision('reject', rejectModal.remark)} className="rounded-md bg-error px-5 py-2 text-sm font-bold text-on-error disabled:opacity-50">Reject</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
